import { useEffect, useState, type FormEvent } from 'react';
import type { CompanyWithCounts, Role } from '@mvcp/contracts';
import { inviteUser, listCompanies } from '../api';
import { FormModal } from './FormModal';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Admin Console → invite a User (Story 1.5, FR-3). System-Administrator-only
 * surface (belt with the API guard). On success it surfaces the activation link
 * (local dev has no real email) so the flow can be completed by hand.
 */
export function InviteUserView(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('compliance_manager');
  const [companyId, setCompanyId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [companies, setCompanies] = useState<CompanyWithCounts[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  /** Whether the invite dialog is open (the form moved out of the page body). */
  const [inviting, setInviting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    listCompanies()
      .then(setCompanies)
      .catch((err: unknown) => {
        setCompanies([]);
        setLoadError(err instanceof Error ? err.message : 'Could not load companies');
      });
  }, []);

  // Only a Compliance Manager is company-scoped. A System Administrator is
  // cross-tenant (FR-30) — the admin invite is email-only, carrying no company;
  // a Vendor carries a vendorId instead.
  const needsCompany = role === 'compliance_manager';

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLink(null);
    if (needsCompany && !companyId) {
      setError('Select a company for this role.');
      return;
    }
    if (role === 'vendor' && !vendorId) {
      setError('A vendor ID is required for the vendor role.');
      return;
    }
    setSubmitting(true);
    try {
      const inv = await inviteUser({
        email,
        role,
        companyId: needsCompany ? companyId || undefined : undefined,
        vendorId: role === 'vendor' ? vendorId || undefined : undefined,
      });
      setLink(inv.activationLink ?? null);
      // Close on SUCCESS only; the link renders on the page behind it.
      setInviting(false);
      setEmail('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="card stack" aria-labelledby="invite-card-heading">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 id="invite-card-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Users
        </h2>
        <button type="button" className="btn-accent" data-surface="accent" onClick={() => setInviting(true)}>
          Invite a User
        </button>
      </div>

      {/* The card would otherwise be a heading and a button with nothing between them —
          the form that used to explain it now lives in the dialog. */}
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Invite a Compliance Manager to a Company, or a System Administrator with no Company.
        They are emailed an activation link.
      </p>

      {inviting && (
      <FormModal title="Invite a User" titleId="invite-heading" onClose={() => setInviting(false)}>
      <form onSubmit={onSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
          <span className="field-label">Email</span>
          <input
            aria-label="Invitee email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={fieldStyle}
          />
        </label>
        <label className="stack" style={{ gap: 4, flex: '0 1 190px' }}>
          <span className="field-label">Role</span>
          <select
            aria-label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            style={fieldStyle}
          >
            <option value="compliance_manager">Compliance Manager</option>
            <option value="system_administrator">System Administrator</option>
            <option value="vendor">Vendor</option>
          </select>
        </label>
        {needsCompany && (
          <label className="stack" style={{ gap: 4, flex: '0 1 200px' }}>
            <span className="field-label">Company</span>
            <select
              aria-label="Company"
              required
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              style={fieldStyle}
            >
              <option value="">Select…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {loadError && (
              <span className="muted" style={{ fontSize: 12 }} role="alert">
                {loadError}
              </span>
            )}
          </label>
        )}
        {role === 'vendor' && (
          <label className="stack" style={{ gap: 4, flex: '0 1 200px' }}>
            <span className="field-label">Vendor ID</span>
            <input
              aria-label="Vendor ID"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              style={fieldStyle}
            />
          </label>
        )}
        <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send invitation'}
        </button>
      </form>
      <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
        {error && (
          <span className="status-pill" data-tone="bad">
            <span className="status-pill__dot" aria-hidden="true" />
            {error}
          </span>
        )}
      </div>
      </FormModal>
      )}

      {/* The activation link is the RESULT of the invite and has to be copied, so it lives
          on the page rather than inside the dialog that produced it. */}
      <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
        {link && (
          <p className="muted" style={{ margin: 0, fontSize: 13, wordBreak: 'break-all' }}>
            Invitation created. Activation link (dev — no email sent):{' '}
            <span data-numeric>{link}</span>
          </p>
        )}
      </div>
    </section>
  );
}
